import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  Alert,
  Badge,
  Button,
  EmptyState,
  HelpTip,
  PageContainer,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { usePodeAcessar } from '@/lib/acesso-store';
import { api } from '@/lib/api';
import { ReposicoesDaRoute } from '@/pages/manutencao/ReposicoesDaRoute';
import { OrcamentosDaManutencao } from '@/pages/manutencao/orcamento/OrcamentosDaManutencao';

import { formatarData, formatTime } from '@/lib/format-date';
import { horaDoAuvo } from '@/lib/manutencao/hora-do-auvo';
/**
 * `Manutenção › Orçamentos` — a fila do que precisa ser orçado.
 *
 * 🔴 **É FILA DE TRABALHO, não painel de gestão.** Quem usa são os
 * colaboradores da Manutenção, e o que importa é *o que eu pego agora*,
 * ordenado pelo que espera há mais tempo.
 *
 * ⚠️ **Sem indicador de margem, sem gráfico, sem KPI.** Numa tela de operação
 * isso é enfeite — e enfeite empurra para baixo a linha que a pessoa veio ler.
 *
 * 🔴 **Cliente sem vínculo aparece igual**, com o nome cru do Auvo e o atalho
 * para resolver. Um pré-orçamento que some porque o cliente não casou é um
 * orçamento que ninguém faz e ninguém sabe que existe.
 */

interface Item {
  id: string;
  taskId: string;
  clienteId: string | null;
  clienteNome: string;
  identificado: boolean;
  descricao: string | null;
  tecnico: string | null;
  dataTarefaIso: string | null;
  taskUrl: string | null;
  esperandoDias: number | null;
}

interface Fila {
  itens: Item[];
  total: number;
  semVinculo: number;
}

const AJUDA =
  'A lista nasce sozinha: quando o técnico responde "sim" a "Necessidade de orçamento?" no ' +
  'questionário do Auvo, o atendimento aparece aqui. O orçamento é feito no Omie — o IGM só ' +
  'acompanha o que falta fazer.';

function esperando(dias: number | null): string {
  if (dias === null) return 'sem data';
  if (dias === 0) return 'hoje';
  if (dias === 1) return 'há 1 dia';
  return `há ${dias} dias`;
}

/** "06/10 às 15:42" no relógio de Brasília, seja qual for o fuso do computador de quem usa. */
function agoraEmBrasilia(d: Date = new Date()): string {
  const dia = formatarData(d, { day: '2-digit', month: '2-digit' });
  const hora = formatTime(d);
  return `${dia} às ${hora}`;
}

/** ⚠️ A cor sobe com a espera. É o que faz a fila ser lida de cima para baixo. */
function corDaEspera(dias: number | null): 'neutral' | 'warning' | 'danger' {
  if (dias === null) return 'neutral';
  if (dias >= 7) return 'danger';
  if (dias >= 3) return 'warning';
  return 'neutral';
}

/**
 * 🔴 Etapa 2 do atendimento com a OS (27/10/2026, mig 782; D57 e C5 do levantamento): os orçamentos que a Manutenção
 * MONTA entram NESTA tela, como segunda fonte — ❌ não numa tela nova (Matheus: *"utilize as telas existentes para não
 * criar duplicidade de tela para fazer a mesma função"*). Quem não tem a porta `manutencao.orcamentos` (no começo, todos
 * menos o admin) vê a fila do Auvo exatamente como antes.
 */
export default function OrcamentosPage() {
  const podeOrcar = usePodeAcessar('manutencao.orcamentos');
  // `?aba=route` abre direto na reposição: é para onde o número "Route sem repor" da Home leva (D71).
  const [params] = useSearchParams();
  const aba = ['manutencao', 'auvo', 'route'].includes(params.get('aba') ?? '') ? (params.get('aba') as string) : 'manutencao';
  if (!podeOrcar) {
    return (
      <PageContainer>
        <PageHeader title="Orçamentos" subtitle="O que os técnicos pediram para orçar, do mais antigo para o mais novo." />
        <FilaDoAuvo />
      </PageContainer>
    );
  }
  return (
    <PageContainer>
      <PageHeader title="Orçamentos" subtitle="Os orçamentos da Manutenção e os pedidos que chegam do Auvo." />
      <Tabs defaultValue={aba}>
        <TabsList seletorNoCelular="Orçamentos">
          {/* Rótulo curto: a 360 px "Orçamentos da Manutenção" empurrava a segunda aba para fora da tela (gate de 28/10).
              O título da página já diz "Orçamentos". */}
          <TabsTrigger value="manutencao">Da Manutenção</TabsTrigger>
          <TabsTrigger value="auvo">Pedidos do Auvo</TabsTrigger>
          {/* D20 (mig 795): a reposição de material à Route mora aqui, como mais uma aba (não uma tela nova). */}
          <TabsTrigger value="route">Reposição da Route</TabsTrigger>
        </TabsList>
        <TabsContent value="manutencao">
          <OrcamentosDaManutencao />
        </TabsContent>
        <TabsContent value="auvo">
          <FilaDoAuvo />
        </TabsContent>
        <TabsContent value="route">
          <ReposicoesDaRoute />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

/** A fila dos pedidos de orçamento do Auvo (o pré-orçamento) — intacta durante o piloto (C5). */
function FilaDoAuvo() {
  const [fila, setFila] = useState<Fila | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const toastUi = useToast();

  const carregar = useCallback(() => {
    setErro(null);
    setCarregando(true);
    api
      .get<Fila>('/api/v1/preorcamento')
      .then(setFila)
      .catch((e: Error) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(carregar, [carregar]);

  /**
   * A2 (INDICADORES-E-CONTRATOS, 06/10/2026): depois de marcar, a tela DIZ quando ficou marcado (hora de
   * Brasília) e oferece desfazer — a linha some da fila, e sem isso um clique na linha errada não tinha volta
   * pela tela (o servidor já sabia desfazer; faltava o botão).
   */
  const [ultimoEnviado, setUltimoEnviado] = useState<{ id: string; cliente: string; quando: string } | null>(null);

  async function marcar(item: Item) {
    setOcupado(item.id);
    setErro(null);
    try {
      const r = await api.post<Fila>(`/api/v1/preorcamento/${item.id}/enviado`, {});
      setFila(r);
      setUltimoEnviado({ id: item.id, cliente: item.clienteNome, quando: agoraEmBrasilia() });
      toastUi.show('Marcado como enviado.', { variant: 'success' });
    } catch (err) {
      // ⚠️ `response`, não `body` — foi o que produziu o "Erro 400" mudo.
      const e = err as Error & { response?: { erro?: string } };
      setErro(e.response?.erro ?? e.message);
    } finally {
      setOcupado(null);
    }
  }

  return (
    <>
      {carregando && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>}
      {erro && <Alert variant="danger">{erro}</Alert>}

      {ultimoEnviado && (
        <Alert variant="success" className="mb-4" data-testid="orcamento-enviado-faixa">
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-2">
            <span>
              <strong>{ultimoEnviado.cliente}</strong> marcado como enviado em {ultimoEnviado.quando}{' '}
              (horário de Brasília).
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="min-h-[44px]"
              data-testid="orcamento-desfazer"
              onClick={async () => {
                const alvo = ultimoEnviado;
                setUltimoEnviado(null);
                try {
                  const r = await api.post<Fila>(`/api/v1/preorcamento/${alvo.id}/desfazer`, {});
                  setFila(r);
                  toastUi.show('Envio desfeito: o orçamento voltou para a fila.', { variant: 'info' });
                } catch (err) {
                  setErro((err as Error).message);
                }
              }}
            >
              Desfazer
            </Button>
          </span>
        </Alert>
      )}

      {fila && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
              {fila.total === 0
                ? 'Nada esperando orçamento.'
                : `${fila.total} ${fila.total === 1 ? 'atendimento espera' : 'atendimentos esperam'} orçamento`}
            </span>
            <HelpTip texto={AJUDA} />
            {fila.semVinculo > 0 && (
              <Badge variant="warning" size="sm" data-testid="orcamentos-sem-vinculo">
                {fila.semVinculo} sem cliente identificado
              </Badge>
            )}
          </div>

          {fila.total === 0 ? (
            <EmptyState
              title="Nenhum orçamento na fila"
              description={
                'Quando um técnico responder "sim" a "Necessidade de orçamento?" no Auvo, o ' +
                'atendimento aparece aqui sozinho.'
              }
              data-testid="orcamentos-vazio"
            />
          ) : (
            <ul className="space-y-3" data-testid="orcamentos-fila">
              {fila.itens.map((i) => (
                <li
                  key={i.id}
                  className="rounded-lg border border-line bg-surface p-4 dark:border-line-dark dark:bg-surface-dark"
                  data-testid="orcamento-item"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-ink-1 dark:text-ink-dark-1">
                        {i.clienteNome}
                      </p>
                      {/* 🔴 Sem vínculo NÃO some: aparece com o aviso e o atalho. */}
                      {!i.identificado && (
                        <p className="mt-1 text-xs text-warning" data-testid="orcamento-sem-vinculo">
                          Cliente ainda não identificado — o nome acima é o do Auvo.{' '}
                          <Link
                            to="/admin/integracoes"
                            className="underline"
                            data-testid="atalho-vinculos"
                          >
                            Resolver em Vínculos
                          </Link>
                        </p>
                      )}
                      {i.descricao && (
                        <p className="mt-1 text-sm text-ink-2 dark:text-ink-dark-2">{i.descricao}</p>
                      )}
                      <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                        {i.tecnico ? `${i.tecnico} · ` : ''}
                        {/* V1: `dataTarefaIso` é o `taskDate` do Auvo, hora de parede de Brasília SEM fuso — nunca
                            passa pelo formatador de fuso (ele lia como UTC e uma tarefa da madrugada mudava de dia). */}
                        {i.dataTarefaIso
                          ? horaDoAuvo(i.dataTarefaIso).data
                          : 'sem data'}
                        {i.taskUrl && (
                          <>
                            {' · '}
                            <a
                              href={i.taskUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="underline"
                            >
                              ver no Auvo
                            </a>
                          </>
                        )}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={corDaEspera(i.esperandoDias)} size="sm">
                        {esperando(i.esperandoDias)}
                      </Badge>
                      <Button
                        size="sm"
                        loading={ocupado === i.id}
                        onClick={() => marcar(i)}
                        data-testid="marcar-enviado"
                      >
                        Marcar como enviado
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-4 text-xs text-ink-3 dark:text-ink-dark-3">
            O orçamento é feito no Omie. Aqui só se acompanha o que ainda falta — e quem marcou
            fica registrado.
          </p>
        </>
      )}
    </>
  );
}
